import {
  AcademicCapIcon,
  ChatAlt2Icon,
  ClipboardCheckIcon,
  MicrophoneIcon,
  UserGroupIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import Link from 'next/link';
import type { ComponentType } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { useLandingT } from './kit';
import { USE_CASE_LINKS, type UseCaseLink } from './pages';

const ICONS: Record<
  UseCaseLink['icon'],
  { icon: ComponentType<{ className?: string }>; tone: string }
> = {
  meeting: {
    icon: UserGroupIcon,
    tone: 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300',
  },
  zoom: {
    icon: VideoCameraIcon,
    tone: 'bg-sky-50 text-sky-600 dark:bg-sky-400/10 dark:text-sky-300',
  },
  interview: {
    icon: ChatAlt2Icon,
    tone: 'bg-rose-50 text-rose-600 dark:bg-rose-400/10 dark:text-rose-300',
  },
  podcast: {
    icon: MicrophoneIcon,
    tone: 'bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-300',
  },
  lecture: {
    icon: AcademicCapIcon,
    tone: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300',
  },
  notetaker: {
    icon: ClipboardCheckIcon,
    tone: 'bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300',
  },
};

/** The kinds of recording with their own page, as an index to browse. */
export default function UseCases({ currentPath }: { currentPath?: string }) {
  const { t } = useLandingT();
  const reveal = useReveal<HTMLUListElement>(0.1);
  const items = USE_CASE_LINKS.filter((item) => item.path !== currentPath);

  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-5'>
          <div className='lg:sticky lg:top-28'>
            <SectionHeading
              size='md'
              title={t('Notes that fit the recording.')}
              tail={t('Not just a wall of text.')}
              lead={t(
                'A meeting needs decisions and next steps, a podcast needs show notes, a class needs points to revise. Each kind of recording gets the notes it needs.',
              )}
            />
          </div>
        </div>

        <ul
          ref={reveal.ref}
          data-reveal={reveal.state}
          role='list'
          className={clsxm(
            styles.reveal,
            'divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] lg:col-span-7 dark:divide-white/[0.08] dark:border-white/[0.08]',
          )}
        >
          {items.map((item, index) => {
            const { icon: Icon, tone: iconTone } = ICONS[item.icon];
            return (
              <li
                key={item.path}
                className={styles.rv}
                style={delay(index * 80)}
              >
                <Link
                  href={item.path}
                  className='group flex items-start gap-5 py-7 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600'
                >
                  <span
                    className={clsxm(
                      'flex h-12 w-12 flex-none items-center justify-center rounded-2xl',
                      iconTone,
                    )}
                  >
                    <Icon className='h-6 w-6' aria-hidden />
                  </span>
                  <span className='min-w-0 flex-1'>
                    <span
                      className={clsxm(
                        'flex items-center gap-2 text-xl font-semibold tracking-tight transition-colors group-hover:text-primary-700 dark:group-hover:text-primary-300',
                        tone.ink,
                      )}
                    >
                      {t(item.title)}
                      <ArrowGlyph className='h-4 w-4 opacity-0 transition duration-200 group-hover:translate-x-0.5 group-hover:opacity-100 rtl:group-hover:-translate-x-0.5' />
                    </span>
                    <span
                      className={clsxm(
                        'mt-1.5 block text-[16px] leading-7',
                        tone.body,
                      )}
                    >
                      {t(item.body)}
                    </span>
                    <span className='mt-3 flex flex-wrap gap-1.5'>
                      {item.outputs.map((output) => (
                        <span
                          key={output}
                          className={clsxm(
                            'rounded-full bg-[#f1f1f7] px-2.5 py-1 text-xs font-medium dark:bg-white/[0.06]',
                            tone.body,
                          )}
                        >
                          {t(output)}
                        </span>
                      ))}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
